import React, { useCallback, useState } from 'react';
import {
  Button, Chip, Divider, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link, useLocation } from 'react-router-dom';
import { Add, Delete, DeleteForever } from '@mui/icons-material';
import {
  NumberEditor,
  TextEditor,
  withStaticSelectEditor,
} from 'shared/components/data-display/editors';
import DataTable from 'shared/components/table/DataTable';
import { createUnit, deleteUnit, searchBuildingUnits } from 'shared/api/units';
import { capitalize, dateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { getUnitPath } from 'shared/routes/building';
import { useRequest } from 'shared/hooks';
import { InfoModal } from 'shared/components/abstract/interactables';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';

/**
 * @param {Object} props
 * @param {number} props.vlanStart
 * @param {number} props.vlanEnd
 * @param {Function} props.onSubmit
 * @param {Object<string, *>} props.newUnit
 * @param {Function} props.setNewUnit
 * @param {boolean} props.isDot1x
 * @param {string} [props.vlanDistributionType=null]
 */
function UnitForm(props) {
  const {
    vlanStart,
    vlanEnd,
    onSubmit,
    newUnit,
    setNewUnit,
    isDot1x,
    vlanDistributionType = null,
  } = props;

  const createUnitDescription = [{
    label: 'Name',
    key: 'name',
    editor: TextEditor,
    required: true,
  }, {
    label: 'VLAN',
    key: 'vlan',
    editor: NumberEditor,
    minimum: vlanStart,
    maximum: vlanEnd,
    required: true,
    skipWhen: () => !isDot1x,
  }, {
    label: 'Management',
    key: 'management',
    editor: withStaticSelectEditor(['GROUP', 'USER']),
    required: true,
    skipWhen: () => !isDot1x,
  }];

  const updateValue = (key, value) => {
    setNewUnit({ ...newUnit, [key]: value });
  };

  return (
    <Grid
      container
      size={12}
      sx={{ px: 2, py: 3, backgroundColor: 'cardBackground' }}
    >
      <Grid
        container
        size={12}
        sx={{ alignItems: 'center', mb: 2, justifyContent: 'space-between' }}
      >
        <Grid>
          <Typography sx={{ color: 'textMuted', fontSize: '1.25rem' }}>
            Create Unit
          </Typography>
        </Grid>
        {isDot1x && vlanDistributionType
          && (
            <Grid>
              <Chip
                color="primary"
                variant="outlined"
                label={`Distribution Type: ${capitalize(vlanDistributionType)}`}
              />
            </Grid>
          )}
      </Grid>
      <Grid
        container
        size={12}
        columnSpacing={1}
        rowSpacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Grid size={{ xs: 12, md: 9 }}>
          <DataFormatter
            descriptions={createUnitDescription}
            editing
            data={newUnit}
            updateValue={updateValue}
            size={{ xs: 12, md: 4 }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 'auto' }}>
          <Button
            startIcon={<Add />}
            disabled={newUnit.name === '' || newUnit.vlan < vlanStart
              || newUnit.vlan > vlanEnd || !newUnit.vlan}
            variant={VARIANTS.contained}
            type="submit"
            onClick={onSubmit}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}

function nextAvailableVlan(units, vlanStart, vlanEnd, vlanDistributionType) {
  // If there are no units or vlans have not been set, return starting value
  if (units == null || units.length === 0 || (vlanStart === 0 && vlanEnd === 0)) {
    return vlanStart;
  }
  let nextVlan = vlanStart;

  if (vlanDistributionType === 'ruckus') {
    const lastUnit = units[units.length - 1];
    return lastUnit.vlan + 1;
  }

  for (let i = 0; i < units.length; i += 1) {
    if (units[i].vlan === nextVlan) {
      nextVlan += 1;
      i = 0;
    }
  }
  // If nextVlan has exceeded vlanEnd, set to zero
  if (nextVlan > vlanEnd) {
    return 0;
  }
  return nextVlan;
}

/**
 * @param {Object} props
 * @param {Object<string, *>} props.buildingState
 */
function UnitSearch(props) {
  const {
    buildingState,
  } = props;

  const {
    data: {
      auth_method: authMethod,
      vlan_range_start: vlanStart,
      vlan_range_end: vlanEnd,
      id: buildingId,
      qos_management: qosManagement,
      units,
      dot1x,
    },
    editEnabled,
  } = buildingState;

  const vlanDistributionType = dot1x ? dot1x.vlan_distribution_type : null;

  const { request, isLoading } = useRequest();
  const { search } = useLocation();

  const [newUnit, setNewUnit] = useState({
    vlan: nextAvailableVlan(units, vlanStart, vlanEnd, vlanDistributionType),
    name: '',
    management: qosManagement,
  });

  const [count, setCount] = useState(0);
  const searchRequest = useCallback(
    (searchStr, accept) => searchBuildingUnits(searchStr, accept, buildingId),
    [buildingId],
  );

  const isDot1x = authMethod.includes('802.1x');
  const onUnitFormSubmit = async (e) => {
    e.preventDefault();

    const needsConfirmation = isDot1x && newUnit.vlan === 0;
    if (needsConfirmation
      && !window.confirm('Setting vlan to 0 will result in internet service configuration issues. Create unit anyway?')) {
      return;
    }

    const response = await request(createUnit({
      building_id: buildingState.data.id,
      name: newUnit.name.trim(),
      vlan: newUnit.vlan,
      qos_management: newUnit.management,
      note: '',
    }), 'Added unit');

    if (!response) return;
    // ensure vlanEnd is never exceeded, otherwise set the next vlan to 0
    const nextVlan = newUnit.vlan < vlanEnd ? newUnit.vlan + 1 : 0;
    setNewUnit({
      ...newUnit,
      name: '',
      vlan: nextVlan,
      management: qosManagement,
    });

    // Force table to reload
    setCount(count + 1);
  };

  const removeUnit = async (unitId) => {
    const response = await request(deleteUnit(unitId), 'Removed unit');
    if (response) {
      setCount(count + 1);
    }
  };

  function generateLink(row) {
    return (
      <Link
        to={`${getUnitPath(row.building_id, row.id)}${search}`}
      >
        {row.name || row.id}
      </Link>
    );
  }

  function generateRemoveBtn(row) {
    return (
      <InfoModal
        actionName="Remove"
        className="trash"
        actionCallback={() => removeUnit(row.id)}
        icon={(row.tenant_count > 0 ? DeleteForever : Delete)}
        description="Are you sure you want to remove this unit?"
        tip="Remove from building"
        title="Remove Unit"
        disabled={row.tenant_count > 0}
        disabledTip="Must remove tenants first"
        color={BUTTON_COLORS.error}
      />
    );
  }

  const columns = [
    {
      name: 'Name',
      formatter: (row) => generateLink(row),
    },
    {
      name: 'Tenants',
      jsonKey: 'tenant_count',
    },
    {
      name: 'Active Tickets',
      formatter: (row) => (row.ticket_associations?.length || 0),
    },
    {
      name: 'Last Updated',
      formatter: (row) => dateString(row.updated_at),
    },
  ];
  if (isDot1x) {
    columns.splice(
      2,
      0,
      {
        name: 'VLAN',
        jsonKey: 'vlan',
      },
      {
        name: 'QOS Management',
        jsonKey: 'qos_management',
      },
    );
  }

  if (editEnabled) {
    columns.push({
      name: 'Remove',
      formatter: (row) => generateRemoveBtn(row),
    });
  }

  return (
    <CardSection>
      <CardControls
        disabled={isLoading}
        editEnabled={editEnabled}
      />
      {editEnabled && (
        <>
          <UnitForm
            onSubmit={onUnitFormSubmit}
            editing={editEnabled}
            vlanStart={vlanStart}
            vlanEnd={vlanEnd}
            newUnit={newUnit}
            setNewUnit={setNewUnit}
            isDot1x={isDot1x}
            vlanDistributionType={vlanDistributionType}
            qosManagement={qosManagement}
          />
          <Divider />
        </>
      )}
      <Grid sx={{ px: 2, py: 1, backgroundColor: 'cardBackground' }}>
        <DataTable
          searchRequest={searchRequest}
          rowId="id"
          className="unit-table-height"
          columns={columns}
          updateCount={count}
        />
      </Grid>
    </CardSection>
  );
}

export default UnitSearch;
